import {gameStatisticsMocks} from '../shared/GameStatisticsService/GameStatisticsService.mocks';
import {DI_TOKENS} from '@/di/di.tokens';
import {DailyGameMocks} from '@/services/shared/DailyGameService/DailyGameService.mocks';
import {GameStatusMocks} from '@/services/shared/GameStatusService/GameStatusService.mocks';
import {createGiftsList, createTicketsList} from '@/services/shared/GiftsService/GiftsService.mock';
import {improvementsStatic} from '@/services/shared/ImprovementsService/ImprovementsService.mock';
import {LeaderBoardMocks} from '@/services/shared/LeaderBoardService/LeaderBoardService.mock';
import {TasksMocks} from '@/services/shared/TasksService/TasksService.mocks';
import {ClickerState, GameOperation} from '@/stores/ClickerStateStore/ClickerStateStore.types';
import {DailyGameConfig} from '@/stores/DailyGameStore/DailyGameStore.types';
import {REWARDS_MOCK} from '@/stores/DailyRewardsStore/DailyRewardsStore.mocks';
import {Reward} from '@/stores/DailyRewardsStore/DailyRewardsStore.types';

import {injected} from 'brandi';

import {HttpAdapterUnsubscribeCallback, wait} from '@specials/project-user';

import {
  DailyGameCheckCipher,
  GameStatisticsResult,
  GameStatusResult,
  GiftsResult,
  IProjectApi,
  ImprovementsResult,
  LeaderBoardResult,
  LeagueResult,
  ProjectApiResponse,
  ProjectApiResponseSuccess,
  TasksResult,
} from '@/services/ProjectApi/types';

import {ProjectDate} from '../ProjectDate/ProjectDate';
import {ClickerStateMocks} from './mocks/ClickerStateMocks';

export class ProjectApiMock implements IProjectApi {
  constructor(
    private readonly dailyGameMocks: DailyGameMocks,
    private readonly clickerStateMocks: ClickerStateMocks,
    private readonly tasksMocks: TasksMocks,
    private readonly leaderBoardMocks: LeaderBoardMocks,
    private readonly gameStatusMocks: GameStatusMocks,
    private readonly projectDate: ProjectDate,
  ) {}

  public onUnauthorizedHttpErrorProject(): HttpAdapterUnsubscribeCallback {
    return () => {};
  }
  public onHttpErrorBackend(): HttpAdapterUnsubscribeCallback {
    return () => {};
  }

  public async getDailyGameConfig(): Promise<ProjectApiResponse<DailyGameConfig>> {
    await wait(500);

    return {
      status: 'ok',
      payload: this.dailyGameMocks.config,
    };
  }

  public async checkDailyGameCipher(cipher: string): Promise<ProjectApiResponse<DailyGameCheckCipher>> {
    await wait(500);

    const isValidFullCipher = this.dailyGameMocks.isValidFullCipher(cipher);

    if (isValidFullCipher) {
      this.clickerStateMocks.addPoints(this.dailyGameMocks.config.reward);
    }

    return {
      status: 'ok',
      payload: {
        isValid: this.dailyGameMocks.isCipherHasString(cipher),
        isFullCipher: isValidFullCipher,
      },
    };
  }

  public async fetchTasks(): Promise<ProjectApiResponse<TasksResult>> {
    await wait(500);

    return Promise.resolve({
      status: 'ok',
      payload: {
        tasks: this.tasksMocks.tasks,
        nextAvailableTaskDate: null,
      },
    });
  }

  public async instantReward(taskId: string): Promise<ProjectApiResponseSuccess> {
    await wait(500);

    const rewards = this.tasksMocks.claimReward(taskId);

    this.clickerStateMocks.addMultiplier(rewards.multiTap);
    this.clickerStateMocks.addRecharges(rewards.energyRecharge);

    return Promise.resolve({status: 'ok'});
  }

  public async seenTasks(): Promise<ProjectApiResponseSuccess> {
    await wait(500);

    return Promise.resolve({status: 'ok'});
  }

  public async clickTaskLink(): Promise<ProjectApiResponseSuccess> {
    await wait(500);

    return Promise.resolve({status: 'ok'});
  }

  public async gameSync(operations: GameOperation[]): Promise<ProjectApiResponse<ClickerState>> {
    await wait(500);

    const newState = this.clickerStateMocks.applyOperations(operations);

    this.leaderBoardMocks.updateUserScore(newState.totalIncomePerHour);

    return Promise.resolve({
      status: 'ok',
      payload: {
        ...this.clickerStateMocks.state,
      },
    });
  }

  public async gameStatus(): Promise<ProjectApiResponse<GameStatusResult>> {
    await wait(500);

    return Promise.resolve({
      status: 'ok',
      payload: {
        status: this.gameStatusMocks.getStatus(),
      },
    });
  }

  public async fetchLeaderBoard(): Promise<ProjectApiResponse<LeaderBoardResult>> {
    await wait(500);

    return Promise.resolve({status: 'ok', payload: this.leaderBoardMocks.getLeaderBoardResult()});
  }

  public async fetchLeague(): Promise<ProjectApiResponse<LeagueResult>> {
    await wait(500);

    return Promise.resolve({status: 'ok', payload: this.leaderBoardMocks.getLeagueResult()});
  }

  public async initLeague(): Promise<ProjectApiResponse<LeagueResult>> {
    await wait(500);

    return Promise.resolve({status: 'ok', payload: this.leaderBoardMocks.getLeagueResult()});
  }

  public async fetchDailyRewards(): Promise<ProjectApiResponse<{rewards: Array<Reward>}>> {
    await wait(500);

    return Promise.resolve({
      status: 'ok',
      payload: {
        rewards: REWARDS_MOCK,
      },
    });
  }

  public async claimDailyReward(): Promise<ProjectApiResponse<void>> {
    await wait(500);

    return Promise.resolve({status: 'ok', payload: undefined});
  }

  public async fetchGifts(): Promise<ProjectApiResponse<GiftsResult>> {
    await wait(500);

    return Promise.resolve({
      status: 'ok',
      payload: {
        gifts: createGiftsList(this.projectDate),
        tickets: createTicketsList(5),
      },
    });
  }

  public async seenGifts(): Promise<ProjectApiResponseSuccess> {
    await wait(500);

    return Promise.resolve({status: 'ok'});
  }

  public async fetchImprovementsStatic(): Promise<ProjectApiResponse<ImprovementsResult>> {
    await wait(500);

    return Promise.resolve({
      status: 'ok',
      payload: {
        improvements: improvementsStatic,
      },
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async setOnboardingStatus(onboardingStatus: string): Promise<ProjectApiResponse<void>> {
    await wait(500);

    return {
      status: 'ok',
      payload: undefined,
    };
  }

  public async claimOnboardingGift(): Promise<ProjectApiResponse<GiftsResult>> {
    await wait(500);

    return Promise.resolve({
      status: 'ok',
      payload: {
        gifts: createGiftsList(this.projectDate).slice(0, 1),
        tickets: [],
      },
    });
  }

  public async updateSettings(): Promise<ProjectApiResponseSuccess> {
    await wait(500);

    return Promise.resolve({status: 'ok'});
  }

  public async fetchStatistics(): Promise<ProjectApiResponse<GameStatisticsResult>> {
    await wait(500);

    return {
      status: 'ok',
      payload: gameStatisticsMocks,
    };
  }
}

injected(
  ProjectApiMock,
  DI_TOKENS.dailyGameMocks,
  DI_TOKENS.clickerStateMocks,
  DI_TOKENS.tasksMocks,
  DI_TOKENS.leaderBoardMocks,
  DI_TOKENS.gameStatusMocks,
  DI_TOKENS.projectDate,
);
